import { fileStorageDownloadUrl } from '@app/pages/admin/orchestration-object-storage.util';

export interface JsonTreeNode {
  id: string;
  keyLabel: string;
  valueLabel: string | null;
  note: string | null;
  downloadUrl: string | null;
  expandable: boolean;
  children: JsonTreeNode[];
}

export interface JsonTreeViewNode {
  id: string;
  keyLabel: string;
  valueLabel: string | null;
  note: string | null;
  downloadUrl: string | null;
  expandable: boolean;
  open: boolean;
  depth: number;
  children: JsonTreeViewNode[];
}

/** Parses a JSON document into a tree. Returns null when the text is not JSON. */
export function parseJsonTree(text: string): JsonTreeNode | null {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }
  try {
    return buildNode('root', '$', JSON.parse(trimmed) as unknown);
  } catch {
    return null;
  }
}

export function expandableIds(node: JsonTreeNode): string[] {
  const ids: string[] = [];
  if (node.expandable) {
    ids.push(node.id);
  }
  for (const child of node.children) {
    ids.push(...expandableIds(child));
  }
  return ids;
}

/** Writes a localized note onto every node whose key is known. */
export function attachJsonTreeNotes(node: JsonTreeNode, noteFor: (key: string) => string | null): JsonTreeNode {
  return {
    ...node,
    note: noteFor(node.keyLabel),
    children: node.children.map(child => attachJsonTreeNotes(child, noteFor)),
  };
}

/** Applies expansion and a case-insensitive key/value/note search. An empty query keeps the expanded set. */
export function projectJsonTree(
  node: JsonTreeNode,
  expanded: ReadonlySet<string>,
  query: string,
  depth = 0,
): JsonTreeViewNode | null {
  const needle = query.trim().toLowerCase();
  const children = node.children
    .map(child => projectJsonTree(child, expanded, query, depth + 1))
    .filter((child): child is JsonTreeViewNode => child !== null);
  const selfMatch = !needle || matches(node, needle);
  if (needle && !selfMatch && children.length === 0) {
    return null;
  }
  return {
    id: node.id,
    keyLabel: node.keyLabel,
    valueLabel: node.valueLabel,
    note: node.note,
    downloadUrl: node.downloadUrl,
    expandable: node.expandable,
    open: node.expandable && (needle ? children.length > 0 : expanded.has(node.id)),
    depth,
    children,
  };
}

function matches(node: JsonTreeNode, needle: string): boolean {
  return node.keyLabel.toLowerCase().includes(needle)
    || (node.valueLabel?.toLowerCase().includes(needle) ?? false)
    || (node.note?.toLowerCase().includes(needle) ?? false);
}

function buildNode(id: string, keyLabel: string, value: unknown): JsonTreeNode {
  if (Array.isArray(value)) {
    return {
      id,
      keyLabel,
      valueLabel: `[${value.length}]`,
      note: null,
      downloadUrl: null,
      expandable: value.length > 0,
      children: value.map((item, index) => buildNode(`${id}.${index}`, String(index), item)),
    };
  }
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    return {
      id,
      keyLabel,
      valueLabel: `{${entries.length}}`,
      note: null,
      downloadUrl: null,
      expandable: entries.length > 0,
      children: entries.map(([key, item]) => buildNode(`${id}.${key}`, key, item)),
    };
  }
  return {
    id,
    keyLabel,
    valueLabel: formatLeaf(keyLabel, value),
    note: null,
    downloadUrl: fileStorageLeafUrl(keyLabel, value),
    expandable: false,
    children: [],
  };
}

const KIB = 1024;
const MIB = 1024 ** 2;
const GIB = 1024 ** 3;

function fileStorageLeafUrl(keyLabel: string, value: unknown): string | null {
  if (typeof value !== 'string' || !/^fileStorage(Item)?Id$/i.test(keyLabel)) {
    return null;
  }
  return fileStorageDownloadUrl(value);
}

function formatLeaf(keyLabel: string, value: unknown): string {
  if (typeof value === 'string') {
    return JSON.stringify(value);
  }
  if (value === null) {
    return 'null';
  }
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0 && /bytes$/i.test(keyLabel)) {
    return formatByteSize(value);
  }
  return String(value);
}

/** Binary size, same steps as database occupancy: B, KiB, MiB, GiB. */
function formatByteSize(bytes: number): string {
  if (bytes >= GIB) {
    return `${(bytes / GIB).toFixed(2)} GiB`;
  }
  if (bytes >= MIB) {
    return `${(bytes / MIB).toFixed(2)} MiB`;
  }
  if (bytes >= KIB) {
    return `${(bytes / KIB).toFixed(2)} KiB`;
  }
  return `${bytes} B`;
}
